import React, { useEffect, useMemo, useState } from 'react';
import { api } from '../services/api';
import ContactDetail from '../components/ContactDetail';
import HotLeadsStories from '../components/HotLeadsStories';

function formatDateTime(value) {
  if (!value) return null;
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return String(value);
    return d.toLocaleString('en-AU');
  } catch {
    return String(value);
  }
}

function getTopHotReason(lead) {
  const s = (lead?.why_hot_summary || '').trim();
  if (s) return s;

  // fallback for older payloads
  const reasons = lead?.hot_reasons;
  if (!Array.isArray(reasons) || reasons.length === 0) return null;
  const top = reasons[0];
  if (!top) return null;

  if (typeof top === 'string') return top;

  const signal = top.signal || top.type || top.name || null;
  const evidence = top.evidence || top.reason || top.detail || null;

  if (signal && evidence) return `${signal}: ${evidence}`;
  return signal || evidence || null;
}

function getConversationSnippet(lead) {
  const s = lead?.conversation_summary;
  if (!s) return null;
  const text = String(s).trim();
  if (!text) return null;
  return text.length > 140 ? `${text.slice(0, 140)}…` : text;
}

function getLastMessageAt(lead) {
  return lead?.last_message_at || lead?.conversation_last_message_at || null;
}

function HotLeads() {
  const [availableLeads, setAvailableLeads] = useState([]);
  const [myLeads, setMyLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [claimingId, setClaimingId] = useState(null);

  // ── S5: Stories-row state ─────────────────────────────────────────────────
  // hotLeadsForStories: the top-N leads for the avatar row (my-hot only)
  // storiesLoading: true while the my-hot-leads fetch is in flight
  // storiesLeadId: the lead currently displayed in the ContactDetail modal
  const [hotLeadsForStories, setHotLeadsForStories] = useState([]);
  const [storiesLoading, setStoriesLoading] = useState(true);
  const [storiesLeadId, setStoriesLeadId] = useState(null);
  const MAX_STORIES = 12;

  // ─────────────────────────────────────────────────────────────────────────

  // View lead modal state
  const [viewingId, setViewingId] = useState(null);
  const [viewingLead, setViewingLead] = useState(null);
  const [viewError, setViewError] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);

  const closeView = () => {
    setViewingId(null);
    setViewingLead(null);
    setViewError(null);
    setStoriesLeadId(null);
  };

  // ── S5: Stories avatar tap → load lead into the existing modal ────────────
  const handleStoriesLeadSelect = async (id) => {
    setStoriesLeadId(id);
    await handleView(id);
  };
  // ─────────────────────────────────────────────────────────────────────────

  const handleView = async (id) => {
    try {
      setViewingId(id);
      setViewingLead(null);
      setViewError(null);
      setViewLoading(true);

      // Use the existing API service; it will include auth headers
      const data = await api.getContact(id);
      setViewingLead(data);
    } catch (e) {
      setViewError(e?.message || String(e));
    } finally {
      setViewLoading(false);
    }
  };

  const load = async () => {
    setLoading(true);
    setError(null);
    setStoriesLoading(true);
    try {
      const [avail, mine] = await Promise.all([
        api.getHotAvailableLeads(),
        api.getMyHotLeads(),
      ]);
      setAvailableLeads(Array.isArray(avail) ? avail : []);
      setMyLeads(Array.isArray(mine) ? mine : []);

      // ── S5: populate stories row — top MAX_STORIES from my hot leads,
      //        sorted by hot_score desc then last_message_at desc ──────────
      const sortedForStories = (Array.isArray(mine) ? [...mine] : [])
        .sort((a, b) => {
          const bs = typeof b.hot_score === 'number' ? b.hot_score : 0;
          const as = typeof a.hot_score === 'number' ? a.hot_score : 0;
          if (bs !== as) return bs - as;
          const ad = new Date(getLastMessageAt(a) || 0).getTime();
          const bd = new Date(getLastMessageAt(b) || 0).getTime();
          return bd - ad;
        })
        .slice(0, MAX_STORIES);
      setHotLeadsForStories(sortedForStories);
      // ─────────────────────────────────────────────────────────────────────
    } catch (e) {
      console.error('Failed to load hot leads:', e);
      setError(e?.message || 'Failed to load hot leads');
      setAvailableLeads([]);
      setMyLeads([]);
      setHotLeadsForStories([]);
    } finally {
      setLoading(false);
      setStoriesLoading(false);
    }
  };

  const handleClaim = async (id) => {
    try {
      setClaimingId(id);
      setError(null);
      await api.claimLead(id);
      await load();
    } catch (e) {
      console.error('Failed to claim lead:', e);
      setError(e?.message || 'Failed to claim lead');
    } finally {
      setClaimingId(null);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(() => {
      load();
    }, 30000);
    return () => clearInterval(id);
  }, []);

  const sortedAvailable = useMemo(() => {
    return [...availableLeads].sort((a, b) => {
      const as = typeof a?.hot_score === 'number' ? a.hot_score : -Infinity;
      const bs = typeof b?.hot_score === 'number' ? b.hot_score : -Infinity;
      if (bs !== as) return bs - as;
      const ad = new Date(getLastMessageAt(a) || 0).getTime();
      const bd = new Date(getLastMessageAt(b) || 0).getTime();
      return bd - ad;
    });
  }, [availableLeads]);

  const sortedMy = useMemo(() => {
    return [...myLeads].sort((a, b) => {
      const as = typeof a?.hot_score === 'number' ? a.hot_score : -Infinity;
      const bs = typeof b?.hot_score === 'number' ? b.hot_score : -Infinity;
      if (bs !== as) return bs - as;
      const ad = new Date(getLastMessageAt(a) || 0).getTime();
      const bd = new Date(getLastMessageAt(b) || 0).getTime();
      return bd - ad;
    });
  }, [myLeads]);

  if (loading) {
    return <div style={styles.loading}>Loading hot leads…</div>;
  }

  return (
    <div style={styles.container}>
      {/* ── S5: Hot Leads Stories Row — mobile quick-switch avatars ─────────── */}
      {/* Rendered on both mobile and desktop; mobile-only class is not needed
          here since the component is compact and unobtrusive on desktop too.
          On mobile it sits at the very top of the lead-card area, above the
          section headers/pills. */}
      <HotLeadsStories
        leads={hotLeadsForStories}
        currentLeadId={storiesLeadId}
        onLeadSelect={handleStoriesLeadSelect}
        isLoading={storiesLoading}
      />
      {/* ──────────────────────────────────────────────────────────────────── */}

      <div style={styles.headerRow}>
        <div>
          <h1 style={styles.title}>Hot Leads</h1>
          <p style={styles.subtitle}>
            Available: {sortedAvailable.length} • Mine: {sortedMy.length}
          </p>
        </div>
        <button onClick={load} style={styles.refreshBtn}>
          Refresh
        </button>
      </div>

      {error && (
        <div style={styles.errorBanner}>
          {error}
          <button onClick={() => setError(null)} style={styles.dismissBtn}>
            ×
          </button>
        </div>
      )}

      {sortedAvailable.length === 0 && sortedMy.length === 0 ? (
        <div style={styles.emptyState}>
          <div style={styles.emptyIcon}>📭</div>
          <div style={styles.emptyText}>No hot leads right now</div>
          <div style={styles.emptySubtext}>When a lead turns hot, it will show here automatically.</div>
        </div>
      ) : (
        <>
          <div style={styles.section}>
            <div style={styles.sectionHeader}>
              <h2 style={styles.sectionTitle}>Available Hot Leads</h2>
              <div style={styles.sectionCount}>{sortedAvailable.length}</div>
            </div>

            {sortedAvailable.length === 0 ? (
              <div style={styles.sectionEmpty}>No available hot leads.</div>
            ) : (
              <div style={styles.list}>
                {sortedAvailable.map((lead) => {
                  const reason = getTopHotReason(lead);
                  const snippet = getConversationSnippet(lead);
                  const lastAt = getLastMessageAt(lead);

                  return (
                    <div key={lead.id} style={styles.card}>
                      <div style={styles.cardTop}>
                        <div style={styles.nameBlock}>
                          <div style={styles.name}>
                            {lead.first_name} {lead.last_name}
                          </div>
                          {lead.company_name ? <div style={styles.company}>{lead.company_name}</div> : null}
                        </div>

                        <div style={styles.score}>
                          <div style={styles.scoreLabel}>Hot score</div>
                          <div style={styles.scoreValue}>{typeof lead.hot_score === 'number' ? lead.hot_score : '—'}</div>
                        </div>
                      </div>

                      {reason ? (
                        <div style={styles.reasonRow}>
                          <span style={styles.reasonBadge}>Why</span>
                          <span style={styles.reasonText}>{reason}</span>
                        </div>
                      ) : null}

                      {snippet ? <div style={styles.snippet}>{snippet}</div> : null}

                      <div style={styles.metaRow}>
                        <div style={styles.metaItem}>
                          <span style={styles.metaLabel}>Last message</span>
                          <span style={styles.metaValue}>{formatDateTime(lastAt) || '—'}</span>
                        </div>

                        <div style={styles.actionsRight}>
                          <button
                            style={styles.claimButton}
                            onClick={() => handleClaim(lead.id)}
                            disabled={claimingId === lead.id}
                          >
                            {claimingId === lead.id ? 'Claiming…' : 'Claim'}
                          </button>
                          <button style={styles.viewButton} onClick={() => handleView(lead.id)}>
                            View →
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div style={styles.section}>
            <div style={styles.sectionHeader}>
              <h2 style={styles.sectionTitle}>My Hot Leads</h2>
              <div style={styles.sectionCount}>{sortedMy.length}</div>
            </div>

            {sortedMy.length === 0 ? (
              <div style={styles.sectionEmpty}>No claimed hot leads.</div>
            ) : (
              <div style={styles.list}>
                {sortedMy.map((lead) => {
                  const reason = getTopHotReason(lead);
                  const snippet = getConversationSnippet(lead);
                  const lastAt = getLastMessageAt(lead);

                  return (
                    <div key={lead.id} style={styles.card}>
                      <div style={styles.cardTop}>
                        <div style={styles.nameBlock}>
                          <div style={styles.name}>
                            {lead.first_name} {lead.last_name}
                          </div>
                          {lead.company_name ? <div style={styles.company}>{lead.company_name}</div> : null}
                        </div>

                        <div style={styles.score}>
                          <div style={styles.scoreLabel}>Hot score</div>
                          <div style={styles.scoreValue}>{typeof lead.hot_score === 'number' ? lead.hot_score : '—'}</div>
                        </div>
                      </div>

                      {reason ? (
                        <div style={styles.reasonRow}>
                          <span style={styles.reasonBadge}>Why</span>
                          <span style={styles.reasonText}>{reason}</span>
                        </div>
                      ) : null}

                      {snippet ? <div style={styles.snippet}>{snippet}</div> : null}

                      <div style={styles.metaRow}>
                        <div style={styles.metaItem}>
                          <span style={styles.metaLabel}>Last message</span>
                          <span style={styles.metaValue}>{formatDateTime(lastAt) || '—'}</span>
                        </div>

                        <button style={styles.viewButton} onClick={() => handleView(lead.id)}>
                          View →
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}

      {/* Lead Details Modal */}
      {viewingId && (
        <div style={styles.modalOverlay} onClick={closeView}>
          <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.modalHeader}>
              <div style={styles.modalTitle}>Lead Details</div>
              <button style={styles.modalClose} onClick={closeView}>
                ✕
              </button>
            </div>

            {viewLoading && <div style={{ color: '#94a3b8' }}>Loading…</div>}
            {viewError && <div style={{ color: '#ef4444' }}>{viewError}</div>}

            {viewingLead && (
              <ContactDetail
                contact={viewingLead}
                onClose={closeView}
                onActivityAdded={() => {}}
                onDeleted={(deletedId) => {
                  setAvailableLeads((prev) => (Array.isArray(prev) ? prev.filter((l) => l.id !== deletedId) : prev));
                  setMyLeads((prev) => (Array.isArray(prev) ? prev.filter((l) => l.id !== deletedId) : prev));
                  closeView();
                }}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: {
    maxWidth: '900px',
    padding: '20px',
  },
  headerRow: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '12px',
    marginBottom: '8px',
  },
  title: {
    color: '#f1f5f9',
    margin: '0 0 6px 0',
    fontSize: '24px',
    fontWeight: '600',
  },
  subtitle: {
    color: '#94a3b8',
    margin: 0,
    fontSize: '14px',
  },
  section: {
    marginTop: '18px',
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    gap: '12px',
    marginBottom: '10px',
  },
  sectionTitle: {
    color: '#e2e8f0',
    margin: 0,
    fontSize: '16px',
    fontWeight: '700',
  },
  sectionCount: {
    color: '#94a3b8',
    fontSize: '12px',
  },
  sectionEmpty: {
    color: '#94a3b8',
    background: '#1e293b',
    borderRadius: '12px',
    padding: '14px 16px',
    border: '1px solid #334155',
  },
  refreshBtn: {
    padding: '8px 16px',
    fontSize: '13px',
    background: 'var(--brand-primary, #3b82f6)',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    minHeight: '44px',
  },
  loading: {
    color: '#94a3b8',
    fontSize: '14px',
    padding: '20px',
  },
  errorBanner: {
    background: 'rgba(239, 68, 68, 0.2)',
    color: '#fca5a5',
    padding: '12px 16px',
    borderRadius: '8px',
    margin: '16px 0',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  dismissBtn: {
    background: 'none',
    border: 'none',
    fontSize: '20px',
    cursor: 'pointer',
    color: '#fca5a5',
  },
  emptyState: {
    textAlign: 'center',
    padding: '64px 32px',
    background: '#1e293b',
    borderRadius: '12px',
    border: '1px solid #334155',
    marginTop: '18px',
  },
  emptyIcon: {
    fontSize: '56px',
    marginBottom: '12px',
  },
  emptyText: {
    color: '#94a3b8',
    fontSize: '16px',
    marginBottom: '8px',
  },
  emptySubtext: {
    color: '#64748b',
    fontSize: '13px',
  },
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    marginTop: '18px',
  },
  card: {
    background: '#1e293b',
    borderRadius: '12px',
    padding: '16px',
    border: '1px solid #334155',
  },
  cardTop: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '12px',
    alignItems: 'flex-start',
    marginBottom: '10px',
  },
  nameBlock: {
    minWidth: 0,
    flex: 1,
  },
  name: {
    color: '#f1f5f9',
    fontSize: '16px',
    fontWeight: '600',
  },
  company: {
    color: '#64748b',
    fontSize: '13px',
    marginTop: '2px',
  },
  score: {
    textAlign: 'right',
    minWidth: '90px',
  },
  scoreLabel: {
    color: '#94a3b8',
    fontSize: '11px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  scoreValue: {
    color: '#fb7185',
    fontSize: '22px',
    fontWeight: '800',
    lineHeight: 1.1,
  },
  reasonRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    marginBottom: '10px',
  },
  reasonBadge: {
    fontSize: '11px',
    fontWeight: '700',
    color: '#fff',
    background: '#fb7185',
    padding: '3px 8px',
    borderRadius: '999px',
  },
  reasonText: {
    color: '#e2e8f0',
    fontSize: '13px',
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  snippet: {
    color: '#cbd5e1',
    fontSize: '13px',
    lineHeight: 1.4,
    background: 'rgba(15, 23, 42, 0.35)',
    border: '1px solid rgba(148, 163, 184, 0.18)',
    padding: '10px 12px',
    borderRadius: '10px',
    marginBottom: '10px',
  },
  modalOverlay: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(0,0,0,0.45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 9999,
    padding: '20px',
  },
  modal: {
    width: 'min(900px, 96vw)',
    maxHeight: '85vh',
    overflow: 'auto',
    background: '#0b1220',
    borderRadius: '12px',
    padding: '16px',
    border: '1px solid #334155',
  },
  modalHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '12px',
  },
  modalTitle: {
    color: '#f1f5f9',
    fontWeight: '700',
    fontSize: '16px',
  },
  modalClose: {
    background: 'transparent',
    border: 'none',
    color: '#94a3b8',
    fontSize: '18px',
    cursor: 'pointer',
  },
  metaRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingTop: '10px',
    borderTop: '1px solid #334155',
  },
  metaItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
  },
  metaLabel: {
    color: '#64748b',
    fontSize: '11px',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
  },
  metaValue: {
    color: '#94a3b8',
    fontSize: '12px',
  },
  actionsRight: {
    display: 'flex',
    gap: '10px',
    alignItems: 'center',
  },
  claimButton: {
    padding: '8px 14px',
    fontSize: '13px',
    background: '#16a34a',
    border: '1px solid rgba(22, 163, 74, 0.5)',
    borderRadius: '8px',
    cursor: 'pointer',
    color: 'white',
    fontWeight: '700',
    minHeight: '40px',
  },
  viewButton: {
    padding: '8px 14px',
    fontSize: '13px',
    background: '#334155',
    border: '1px solid #475569',
    borderRadius: '8px',
    cursor: 'pointer',
    color: '#e2e8f0',
    fontWeight: '600',
    minHeight: '40px',
  },
};

export default HotLeads;
